/**
 * Shared date helpers for payment plan / credit installment scheduling.
 *
 * The suggested first due date follows the pre-overhaul anchor (latest king invoice
 * due date + 1 month with end-of-month clamping), but rolls forward by whole months
 * as needed so the suggestion is never in the past. When no invoice is available the
 * fallback is `today + 1 month`, matching the pre-overhaul `addMonth(new Date())`
 * behavior.
 */

import { addMonths, startOfDay } from 'date-fns'
import { formatDateForApi } from '../../../../../util/string'

/**
 * Add `offset` whole months to `date`, clamping to the end of the target month when the
 * source day-of-month doesn't exist there (e.g. Jan 31 + 1 month → Feb 28 in a non-leap
 * year). `date-fns`'s `addMonths` already performs this clamping; this thin wrapper is
 * exported for call sites that want an explicit name describing the intent.
 *
 * IMPORTANT: to preserve day-of-month cadence across multiple steps (e.g. Jan 31 → Jan 31,
 * Feb 28, Mar 31, Apr 30 instead of drifting to the 28th after passing through February),
 * anchor on the original date and vary the offset, rather than chaining single-step calls.
 */
export function addMonthsClamped(date: Date, offset: number): Date {
    return addMonths(date, offset)
}

/**
 * Parse a "YYYY-MM-DD" or ISO date/datetime string as local midnight. Returns null
 * when the input is empty or cannot be parsed.
 */
export function parseIsoDateLocal(isoDateOrDatetime: string | undefined | null): Date | null {
    if (!isoDateOrDatetime) return null
    const datePart = isoDateOrDatetime.slice(0, 10)
    const parsed = new Date(datePart + 'T00:00:00')
    return isNaN(parsed.getTime()) ? null : parsed
}

/**
 * Compute a "never in the past" suggested first-installment due date.
 *
 * When the latest king invoice due date is available, start from `invoiceDueDate + 1 month`
 * (the pre-overhaul anchor) and keep adding whole months from the *original* invoice anchor
 * — preserving day-of-month via end-of-month clamping — until the resulting date is on or
 * after `today`. Anchoring from the invoice rather than walking month-by-month keeps the
 * tenant's natural billing-day cadence even across multi-month gaps (e.g. Jan 30 stays
 * at "the 30th" rather than drifting to the 28th after passing through February).
 *
 * When no invoice due date is available, fall back to `today + 1 month`.
 *
 * @param invoiceDueDateIso ISO date/datetime string of the latest king invoice due date, or undefined.
 * @param today Reference "now" — defaults to new Date(), parameterized for testability.
 * @returns "YYYY-MM-DD" date string.
 */
export function computeSuggestedFirstDueDate(
    invoiceDueDateIso: string | undefined,
    today: Date = new Date()
): string {
    const todayMidnight = startOfDay(today)
    const invoiceDate = parseIsoDateLocal(invoiceDueDateIso)
    if (invoiceDate == null) {
        return formatDateForApi(addMonthsClamped(todayMidnight, 1)) ?? ''
    }
    // Guard against runaway loops for wildly-stale invoices: 240 months (20 years) is
    // more than enough headroom for any realistic tenant record.
    const maxIterations = 240
    let months = 1
    let candidate = addMonths(invoiceDate, months)
    while (candidate < todayMidnight && months < maxIterations) {
        months++
        candidate = addMonths(invoiceDate, months)
    }
    return formatDateForApi(candidate) ?? ''
}
